import { useSignalR } from '../components/providers/SignalRProvider.vue'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  StakeholderContactAddedSignalRPayload,
  StakeholderContactUpdatedSignalRPayload,
  StakeholderContactDeletedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type StakeholderContactsEventHandlers = {
  onContactAdded?: (
    payload: SignalRReceiveEvent<StakeholderContactAddedSignalRPayload>
  ) => void
  onContactUpdated?: (
    payload: SignalRReceiveEvent<StakeholderContactUpdatedSignalRPayload>
  ) => void
  onContactDeleted?: (
    payload: SignalRReceiveEvent<StakeholderContactDeletedSignalRPayload>
  ) => void
}

export function useStakeholderContactsSignalREvents(
  handlers?: StakeholderContactsEventHandlers
) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToContactAdded(
    cb: (
      payload: SignalRReceiveEvent<StakeholderContactAddedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderContactAddedSignalRPayload>(
      signalREvent.Stakeholder.V1.StakeholderContactAdded,
      cb,
      options
    )
  }

  function subscribeToContactUpdated(
    cb: (
      payload: SignalRReceiveEvent<StakeholderContactUpdatedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderContactUpdatedSignalRPayload>(
      signalREvent.Stakeholder.V1.StakeholderContactUpdated,
      cb,
      options
    )
  }

  function subscribeToContactDeleted(
    cb: (
      payload: SignalRReceiveEvent<StakeholderContactDeletedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<StakeholderContactDeletedSignalRPayload>(
      signalREvent.Stakeholder.V1.StakeholderContactRemoved,
      cb,
      options
    )
  }

  function subscribeToContactsEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []

      if (handlers.onContactAdded) {
        unsubscribeFunctions.push(
          subscribeToContactAdded(handlers.onContactAdded)
        )
      }

      if (handlers.onContactUpdated) {
        unsubscribeFunctions.push(
          subscribeToContactUpdated(handlers.onContactUpdated)
        )
      }

      if (handlers.onContactDeleted) {
        unsubscribeFunctions.push(
          subscribeToContactDeleted(handlers.onContactDeleted)
        )
      }

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromContactsEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToContactsEvents,
    unsubscribeFromContactsEvents
  }
}
